import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoStockBioInputEntries from "config/demo/stockEntryBioinput.json";
import StockBioInputEntryForm from "components/agro/stockBioEntryInputs/StockBioEntryInputForm";

const StockBioInputEntries = () => {
  const [entries] = useState((demoStockBioInputEntries && demoStockBioInputEntries.slice) ? demoStockBioInputEntries.slice(0, 10) : []);
  const [entryDetails, setEntryDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      entries.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={entryDetails?.id === item.id ? strings.entries.hide : strings.entries.show}
              icon={entryDetails?.id === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                entryDetails?.id === item.id ? setEntryDetails(null) : setEntryDetails(item);
              }}
            />
            <ActionIcon
              name={strings.entries.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.entries.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.prescriptionName || item.inputName || item.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, entryDetails?.id]
  );

  const openModal = (action, id = null) => setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    { key: "date", label: strings.table.headers.date, _style: { width: "18%" } },
    { key: "warehouseId", label: strings.table.headers.warehouse, _style: { width: "20%" } },
    { key: "inputId", label: strings.table.headers.inputName, _style: { width: "20%" } },
    { key: "preparationUnits", label: strings.table.headers.preparationUnits, _style: { width: "10%" } },
    { key: "actions", label: "", _style: { width: "10%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.entries.addEntry}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField label={strings.table.search.label} placeholder={strings.table.search.placeholder} />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!entryDetails?.id} style={styles.collapseCard}>
          <CCard key={entryDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">{strings.details.title} "{entryDetails?.prescriptionName || entryDetails?.inputName || entryDetails?.id}"</h4>

              <p className="text-muted"><b>{strings.details.date}:</b> {entryDetails?.date ? moment(entryDetails.date).format("YYYY-MM-DD") : "-"}</p>

              <p className="text-muted"><b>{strings.details.warehouse}:</b> {entryDetails?.warehouseName ?? entryDetails?.warehouseId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.performedBy}:</b> {entryDetails?.performedById ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.prescription}:</b> {entryDetails?.prescriptionName ?? entryDetails?.prescriptionId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.prescribingAgronomist}:</b> {entryDetails?.prescribingAgronomistId ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.preparationUnits}:</b> {entryDetails?.preparationUnits ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.recipeDetails}:</b> {entryDetails?.recipeDetails ?? "-"}</p>

              <hr />

              <p className="text-muted"><b>{strings.details.inputId}:</b> {entryDetails?.inputId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.inputName}:</b> {entryDetails?.inputName ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.availableInventoryQuantity}:</b> {entryDetails?.availableInventoryQuantity ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.inputInventoryUnit}:</b> {entryDetails?.inputInventoryUnit ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.inputQuantityToUse}:</b> {entryDetails?.inputQuantityToUse ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.applicationUnit}:</b> {entryDetails?.applicationUnit ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.preparationProcedure}:</b> {entryDetails?.preparationProcedure ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.photoEvidence}:</b> {(Array.isArray(entryDetails?.photoEvidence) ? entryDetails.photoEvidence.join(", ") : entryDetails?.photoEvidence) ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.expirationDate}:</b> {entryDetails?.expirationDate ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.storageWarehouse}:</b> {entryDetails?.storageLocationWarehouseName ?? entryDetails?.storageLocationWarehouseId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.storageShelf}:</b> {entryDetails?.storageShelf ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.storageLevel}:</b> {entryDetails?.storageLevel ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.additionalObservations}:</b> {entryDetails?.additionalObservations ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.createdAt}:</b> {entryDetails?.createdAt ? moment(entryDetails.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, { name: modalDetails.itemName })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal size="xl" alignment="center" visible={modalForm.visible} onClose={() => setModalForm({ visible: false })} backdrop="static">
          <CModalHeader>
            <CModalTitle>{modalForm.action === "create" ? strings.modalForm.addTitle : strings.modalForm.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <StockBioInputEntryForm
                type={modalForm.action}
                initialValues={modalForm.action === "update" ? entries.find((e) => e.id === modalForm.itemId) || {} : {}}
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Bioinsumos",
    entries: {
      addEntry: "Ingresar Bioinsumo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        warehouse: "Finca / Almacén",
        prescription: "Receta",
        inputName: "Insumo",
        preparationUnits: "Unidades preparación",
        search: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Ingreso Bioinsumo",
      date: "Fecha",
      warehouse: "Finca / Almacén",
      performedBy: "Persona que realiza",
      prescription: "Receta agrónomo",
      prescribingAgronomist: "Agrónomo que receta",
      preparationUnits: "Unidades de preparación",
      recipeDetails: "Detalle receta",
      inputId: "Insumo (ID)",
      inputName: "Insumo",
      availableInventoryQuantity: "Cantidad disponible",
      inputInventoryUnit: "Unidad de inventario",
      inputQuantityToUse: "Cantidad a usar",
      applicationUnit: "Unidad de aplicación",
      preparationProcedure: "Procedimiento de preparación",
      photoEvidence: "Evidencia fotográfica",
      expirationDate: "Fecha de vencimiento",
      storageWarehouse: "Almacén (almacenamiento)",
      storageShelf: "Estante",
      storageLevel: "Nivel",
      additionalObservations: "Observaciones adicionales",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el registro {name}?",
    },
    modalForm: {
      addTitle: "Registrar Bioinsumo",
      updateTitle: "Actualizar Registro Bioinsumo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default StockBioInputEntries;
